import { useEffect, useMemo, useState } from 'react'
import { AdminLayout, useAdminAccess } from '../../components/AdminLayout'
import { useLanguage } from '../../contexts/LanguageContext'
import { requestApi } from '../../lib/adminApi'

type ConsumptionRecord = {
  id: number
  user_id: string
  username?: string | null
  product_id?: string | null
  product_name?: string | null
  deduction_source: string
  unit_price: string
  created_at: string
}

type ConsumptionResponse = {
  records: ConsumptionRecord[]
  total: number
  limit: number
  offset: number
}

const AdminConsumptionContent = () => {
  const { t } = useLanguage()
  const { isGranted } = useAdminAccess()
  const [records, setRecords] = useState<ConsumptionRecord[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [userIdFilter, setUserIdFilter] = useState('')
  const [offset, setOffset] = useState(0)
  const limit = 20

  const totalPages = useMemo(() => Math.ceil(total / limit), [total, limit])
  const currentPage = useMemo(() => Math.floor(offset / limit) + 1, [offset, limit])

  const fetchRecords = () => {
    setLoading(true)
    setError('')
    const params = new URLSearchParams({
      limit: String(limit),
      offset: String(offset)
    })
    if (userIdFilter.trim()) {
      params.set('user_id', userIdFilter.trim())
    }
    requestApi<ConsumptionResponse>(`/api/v1/admin/consumption?${params.toString()}`)
      .then((payload) => {
        setRecords(payload.records || [])
        setTotal(payload.total || 0)
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : t('admin.consumption.error'))
      })
      .finally(() => {
        setLoading(false)
      })
  }

  useEffect(() => {
    if (!isGranted) return
    fetchRecords()
  }, [offset, isGranted])

  const handleSearch = () => {
    setOffset(0)
    fetchRecords()
  }

  const formatDate = (value: string) => {
    try {
      return new Date(value).toLocaleString()
    } catch (error) {
      return value
    }
  }

  return (
    <div className="space-y-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="flex gap-2">
            <input
              className="border border-slate-200 rounded-lg px-3 py-2 text-sm"
              value={userIdFilter}
              onChange={(e) => setUserIdFilter(e.target.value)}
              placeholder={t('admin.consumption.placeholders.userId')}
            />
            <button
              onClick={handleSearch}
              className="px-3 py-2 text-xs font-semibold border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50"
            >
              {t('admin.consumption.search')}
            </button>
          </div>
        </div>

        {loading && <p className="text-sm text-slate-400">{t('admin.consumption.loading')}</p>}
        {error && <p className="text-sm text-rose-500">{error}</p>}
        {!loading && !error && (
          <div className="overflow-x-auto border border-slate-200 rounded-2xl">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs text-slate-400 uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-3 text-left">{t('admin.consumption.table.user')}</th>
                  <th className="px-4 py-3 text-left">{t('admin.consumption.table.product')}</th>
                  <th className="px-4 py-3 text-left">{t('admin.consumption.table.source')}</th>
                  <th className="px-4 py-3 text-left">{t('admin.consumption.table.unitPrice')}</th>
                  <th className="px-4 py-3 text-left">{t('admin.consumption.table.createdAt')}</th>
                </tr>
              </thead>
              <tbody>
                {records.map((record) => (
                  <tr key={record.id} className="border-t border-slate-100">
                    <td className="px-4 py-3">
                      <div className="font-semibold text-slate-700">{record.username || record.user_id}</div>
                      <div className="text-xs text-slate-400">{record.user_id}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      <div>{record.product_name || '-'}</div>
                      <div className="text-xs text-slate-400">{record.product_id || '-'}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{record.deduction_source}</td>
                    <td className="px-4 py-3 text-slate-600">{record.unit_price}</td>
                    <td className="px-4 py-3 text-slate-600">{formatDate(record.created_at)}</td>
                  </tr>
                ))}
                {records.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-4 py-6 text-center text-sm text-slate-400">
                      {t('admin.consumption.empty')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-between text-xs text-slate-500">
            <span>{t('admin.consumption.pagination').replace('{current}', String(currentPage)).replace('{total}', String(totalPages))}</span>
            <div className="flex gap-2">
              <button
                onClick={() => setOffset(Math.max(0, offset - limit))}
                disabled={offset === 0}
                className="px-3 py-1 border border-slate-200 rounded-lg disabled:opacity-50"
              >
                {t('admin.consumption.prev')}
              </button>
              <button
                onClick={() => setOffset(Math.min((totalPages - 1) * limit, offset + limit))}
                disabled={currentPage >= totalPages}
                className="px-3 py-1 border border-slate-200 rounded-lg disabled:opacity-50"
              >
                {t('admin.consumption.next')}
              </button>
            </div>
          </div>
        )}
      </div>
  )
}

export const AdminConsumptionScreen = () => {
  const { t } = useLanguage()

  return (
    <AdminLayout title={t('admin.consumption.title')} description={t('admin.consumption.desc')}>
      <AdminConsumptionContent />
    </AdminLayout>
  )
}
